import { useState, useEffect, useRef } from 'react';
import { KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import { useRequestHarborDeletionMutation } from '@/services/HarborManagementService';
import clsx from 'clsx';

interface HarborDeletionRequestModalProps {
  isOpen: boolean;
  harborId: string;
  harborName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const HarborDeletionRequestModal = ({
  isOpen,
  harborId,
  harborName,
  onClose,
  onSuccess,
}: HarborDeletionRequestModalProps) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [requestDeletion] = useRequestHarborDeletionMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!reason.trim()) {
      enqueueSnackbar('Please provide a reason for deletion', {
        variant: 'error',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const call = requestDeletion({
        harbor_id: harborId,
        data: { reason: reason },
      });

      if (call && typeof call.unwrap === 'function') {
        await call.unwrap();
      } else {
        await call;
      }

      enqueueSnackbar('Deletion request submitted successfully', {
        variant: 'success',
      });
      onSuccess();
      onClose();
    } catch (e: unknown) {
      const error = e as { data?: { message?: string } };
      enqueueSnackbar(
        error?.data?.message || 'Failed to submit deletion request',
        {
          variant: 'error',
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      const focusTarget = textareaRef.current || modalRef.current;
      if (focusTarget) {
        const timer = setTimeout(() => {
          focusTarget.focus();
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, harborId]);

  return (
    <div
      ref={modalRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      className="w-full max-w-md bg-gray-100 rounded-2xl p-6 shadow-xl mx-auto relative"
      aria-labelledby="deletion-request-title"
    >
      <button
        type="button"
        onClick={onClose}
        disabled={isSubmitting}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 disabled:opacity-50"
        aria-label="Close modal"
      >
        <KeenIcon type="filled" icon="close" className="h-6 w-6" />
      </button>

      <h3
        id="deletion-request-title"
        className="text-lg font-medium leading-6 text-gray-900 border-b pb-4"
      >
        Request Harbor Deletion
      </h3>

      <form onSubmit={handleSubmit} className="mt-4">
        <div className="mb-4">
          <label htmlFor="harbor-name" className="label">
            <span className="label-text font-medium">Harbor</span>
          </label>
          <input
            id="harbor-name"
            className={clsx('input input-bordered w-full bg-light-active')}
            value={harborName}
            readOnly
          />
        </div>

        <div className="mb-6">
          <label className="label">
            <span className="label-text font-medium">
              Reason for Deletion <span className="text-red-500">*</span>
            </span>
          </label>
          <textarea
            ref={textareaRef}
            autoFocus
            className={clsx('textarea textarea-bordered w-full', {
              'border-red-300 focus:border-red-500':
                !reason.trim() && isSubmitting,
            })}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="Please provide a detailed reason for requesting deletion of this harbor..."
            disabled={isSubmitting}
            required
          />
          {!reason.trim() && (
            <p className="text-sm text-gray-600 mt-1">
              Please explain why this harbor should be deleted (e.g.,
              permanently closed, duplicate entry, etc.)
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={clsx('btn btn-sm', {
              'btn-primary': reason.trim(),
              'btn-disabled': !reason.trim() || isSubmitting,
            })}
            disabled={!reason.trim() || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="loading loading-spinner loading-xs"></span>
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </button>
        </div>
      </form>

      <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-950 contrast:bg-yellow-950 border border-yellow-200 dark:border-yellow-900 contrast:border-yellow-900 rounded-lg">
        <div className="flex">
          <KeenIcon
            type="filled"
            icon="warning"
            className="h-5 w-5 text-yellow-400 dark:text-yellow-200 contrast:text-yellow-200"
          />
          <div className="ml-3">
            <p className="text-sm text-yellow-800 dark:text-yellow-200 contrast:text-yellow-200">
              <strong>Note:</strong> This will request soft deletion of the
              harbor. The harbor will be hidden from public view but can be
              recovered by an administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
